/**
 * pmtilesCache.ts
 *
 * Centralized persistent CacheStorage and memory cache management for PMTiles.
 * Provides unified helpers for reading, writing, and preloading PMTiles headers
 * and viewport tiles into the browser cache for smooth animation playback.
 */

import { getOrCreatePMTiles } from '../config';

export const BASEMAP_CACHE_NAME = 'gw-basemap-tiles-v1';
export const WEATHER_CACHE_NAME = 'gw-weather-tiles-v4';
export const WEATHER_CACHE_MAX_AGE_MS = 60 * 60 * 1000; // 60 minutes TTL

// Clean up deprecated cache versions containing obsolete/corrupt tiles or headers
if (typeof caches !== 'undefined') {
  caches.delete('gw-weather-tiles-v3').catch(() => { });
  caches.delete('gw-weather-tiles-v2').catch(() => { });
  caches.delete('gw-weather-tiles-v1').catch(() => { });
}

export const pmtilesHeaderMemoryCache = new Map<string, any>();

export async function getPersistentCachedTile(
  cacheName: string,
  cacheKey: string,
  maxAgeMs?: number
): Promise<{ data: any } | null> {
  if (typeof caches === 'undefined') return null;
  try {
    const cache = await caches.open(cacheName);
    const cachedRes = await cache.match(cacheKey);
    if (!cachedRes) return null;

    if (maxAgeMs && maxAgeMs > 0) {
      const dateHeader = cachedRes.headers.get('x-cache-timestamp');
      if (dateHeader) {
        const cacheTime = parseInt(dateHeader, 10);
        if (Date.now() - cacheTime > maxAgeMs) {
          await cache.delete(cacheKey);
          return null;
        }
      }
    }

    const contentType = cachedRes.headers.get('content-type') || '';
    if (contentType.includes('json')) {
      const text = await cachedRes.text();
      return { data: JSON.parse(text) };
    }
    const buf = await cachedRes.arrayBuffer();
    return { data: new Uint8Array(buf) };
  } catch {
    return null;
  }
}

export interface CacheableBlobItem {
  blob: Blob;
  contentType: string;
}

/**
 * Creates an independent Blob snapshot synchronously from the given data.
 * Must be called synchronously BEFORE passing the data back to MapLibre GL,
 * because MapLibre transfers the underlying ArrayBuffer to a Web Worker
 * via postMessage, detaching the buffer on the main thread.
 */
export function createCacheableBlob(data: any, isJson: boolean = false): CacheableBlobItem | null {
  if (!data) return null;
  try {
    if (isJson) {
      return {
        blob: new Blob([JSON.stringify(data)], { type: 'application/json' }),
        contentType: 'application/json'
      };
    }
    if (data && data.blob instanceof Blob) {
      return {
        blob: data.blob,
        contentType: data.contentType || 'application/octet-stream'
      };
    }
    if (data instanceof Blob) {
      return {
        blob: data,
        contentType: data.type || 'application/octet-stream'
      };
    }
    if (data instanceof ArrayBuffer) {
      if (data.byteLength === 0 || (data as any).detached) return null;
      // Synchronously clone the slice of data before MapLibre transfers it to Web Worker
      const cloned = data.slice(0);
      return {
        blob: new Blob([cloned], { type: 'application/octet-stream' }),
        contentType: 'application/octet-stream'
      };
    }
    if (ArrayBuffer.isView(data)) {
      if (data.byteLength === 0 || !data.buffer || (data.buffer as any).detached) return null;
      // Synchronously clone the exact view bytes safely
      try {
        const copy = new Uint8Array(data.byteLength);
        copy.set(new Uint8Array(data.buffer, data.byteOffset, data.byteLength));
        return {
          blob: new Blob([copy.buffer], { type: 'application/octet-stream' }),
          contentType: 'application/octet-stream'
        };
      } catch {
        return null;
      }
    }
  } catch {
    return null;
  }
  return null;
}

export async function setPersistentCachedTile(
  cacheName: string,
  cacheKey: string,
  dataOrItem: any,
  isJson: boolean = false
): Promise<void> {
  if (typeof caches === 'undefined' || !dataOrItem) return;
  try {
    // Clone or validate BEFORE any async suspension point
    const item = (dataOrItem && dataOrItem.blob instanceof Blob)
      ? dataOrItem as CacheableBlobItem
      : createCacheableBlob(dataOrItem, isJson);
    if (!item) return;

    const cache = await caches.open(cacheName);
    const headers = new Headers({
      'Content-Type': item.contentType,
      'x-cache-timestamp': String(Date.now())
    });

    const response = new Response(item.blob, { headers });
    await cache.put(cacheKey, response);
  } catch {
    // Ignore storage quota limits or detached buffers
  }
}

/**
 * Pre-fetches and caches PMTiles header JSON.
 */
export async function preloadPmtilesHeader(archiveUrl: string): Promise<any> {
  if (!archiveUrl) return null;
  const cleanUrl = archiveUrl.startsWith('pmtiles://') ? archiveUrl.substring('pmtiles://'.length) : archiveUrl;
  const pmtilesUrl = `pmtiles://${cleanUrl}`;
  const cacheKey = `pmtiles_v1_${pmtilesUrl}_json`;

  if (pmtilesHeaderMemoryCache.has(pmtilesUrl)) {
    return pmtilesHeaderMemoryCache.get(pmtilesUrl);
  }

  const diskHit = await getPersistentCachedTile(WEATHER_CACHE_NAME, cacheKey, WEATHER_CACHE_MAX_AGE_MS);
  if (diskHit && diskHit.data) {
    pmtilesHeaderMemoryCache.set(pmtilesUrl, diskHit);
    return diskHit.data;
  }

  try {
    const instance = getOrCreatePMTiles(cleanUrl);
    const header = await instance.getHeader();
    if (header) {
      const tileJson = {
        tilejson: '3.0.0',
        minzoom: 0,
        maxzoom: header.maxZoom && header.maxZoom > 0 ? header.maxZoom : 5,
        bounds: [header.minLon, header.minLat, header.maxLon, header.maxLat],
        tiles: [`pmtiles://${cleanUrl}/{z}/{x}/{y}`]
      };
      pmtilesHeaderMemoryCache.set(pmtilesUrl, { data: tileJson });
      await setPersistentCachedTile(WEATHER_CACHE_NAME, cacheKey, tileJson, true);
      return tileJson;
    }
  } catch {
    // Ignore fetch error
  }
  return null;
}

/**
 * Pre-fetches and caches an individual PMTiles raster tile.
 */
export async function preloadPmtilesTile(
  archiveUrl: string,
  z: number,
  x: number,
  y: number
): Promise<boolean> {
  if (!archiveUrl) return false;
  const cleanUrl = archiveUrl.startsWith('pmtiles://') ? archiveUrl.substring('pmtiles://'.length) : archiveUrl;
  const tileUrl = `pmtiles://${cleanUrl}/${z}/${x}/${y}`;
  const cacheKey = `pmtiles_v1_${tileUrl}_tile`;

  const diskHit = await getPersistentCachedTile(WEATHER_CACHE_NAME, cacheKey, WEATHER_CACHE_MAX_AGE_MS);
  if (diskHit && diskHit.data) {
    return true;
  }

  try {
    const instance = getOrCreatePMTiles(cleanUrl);
    const res = await instance.getZxy(z, x, y);
    if (res && res.data && (res.data as any).byteLength > 0) {
      await setPersistentCachedTile(WEATHER_CACHE_NAME, cacheKey, res.data, false);
      return true;
    }
  } catch {
    // Ignore fetch error
  }
  return false;
}
